import {
  ArrowRight,
  ExternalLink,
  FileText,
  Globe,
  Grid3x3,
  Image,
  LayoutDashboard,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  Presentation,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, useConfirm, useToast } from "@/components/shared";
import { featureFlags } from "@/config/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { appService } from "@/lib/services";
import type { App } from "@/types";

// ============================================================================
// Built-in apps (hardcoded, not editable)
// ============================================================================

interface BuiltInAppEntry {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  category: string;
  featured?: boolean;
  comingSoon?: boolean;
  route?: string;
  externalUrl?: string;
  isBuiltIn: true;
}

const builtInApps: BuiltInAppEntry[] = [
  {
    id: "sowift",
    name: "SoWift",
    description:
      "Generate polished Statements of Work from meeting notes, Slack messages, and project briefs. AI-powered gap analysis and structured document generation.",
    icon: FileText,
    category: "Document Generation",
    featured: true,
    route: "/apps/sowift",
    isBuiltIn: true,
  },
  {
    id: "presentations",
    name: "Presentations",
    description:
      "Create compelling client presentations from project data, proposals, and key insights. AI-assisted slide generation and design.",
    icon: Presentation,
    category: "Document Generation",
    route: "/apps/presentations",
    isBuiltIn: true,
  },
];

// Built-in entry for the Micro Apps gallery — only surfaced when the flag is on.
const MICRO_APPS_ENTRY: BuiltInAppEntry = {
  id: "micro-apps",
  name: "Micro Apps",
  description: "Publish, share, and discover generated dashboards as first-class, live micro apps.",
  icon: LayoutDashboard,
  category: "Document Generation",
  featured: true,
  route: "/apps/view",
  isBuiltIn: true,
};

// ============================================================================
// Unified app entry type
// ============================================================================

interface AppEntry {
  id: string;
  name: string;
  description: string;
  icon?: React.ElementType;
  iconColor?: string;
  iconUrl?: string;
  category: string;
  featured?: boolean;
  comingSoon?: boolean;
  route?: string;
  externalUrl?: string;
  isBuiltIn: boolean;
  uuid?: string; // for custom apps
}

// ============================================================================
// Color palette for custom apps
// ============================================================================

const APP_COLORS = [
  "#EC622B", // mutual orange
  "#7C16F5", // mutual purple
  "#06b6d4", // cyan
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#6366f1", // indigo
  "#84cc16", // lime
];

const categories = ["All", "Document Generation", "External Tools"];

// ---------------------------------------------------------------------------
// Known-app logo detection — uses Google's favicon service for a reliable,
// high-quality icon (128×128 PNG) for any external URL with a domain.
// ---------------------------------------------------------------------------

/**
 * Extract the registrable (base) domain from a hostname.
 * e.g. "centurytechgroupcom-my.sharepoint.com" → "sharepoint.com"
 */
function extractBaseDomain(hostname: string): string {
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname;

  const shortSlds = new Set(["co", "com", "org", "net", "gov", "edu", "ac"]);
  if (parts.length >= 3 && shortSlds.has(parts[parts.length - 2])) {
    return parts.slice(-3).join(".");
  }
  return parts.slice(-2).join(".");
}

function detectIconUrlFromAppUrl(appUrl: string): string | null {
  try {
    const hostname = new URL(appUrl).hostname.toLowerCase();
    if (!hostname) return null;
    const domain = extractBaseDomain(hostname);
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  } catch {
    // invalid URL, ignore
  }
  return null;
}

/**
 * Renders an image with a fallback Globe icon if the image fails to load.
 */
function AppIconImage({
  src,
  color,
  sizeClasses,
  iconSizeClasses,
}: Readonly<{
  src: string;
  color: string;
  sizeClasses: string;
  iconSizeClasses: string;
}>) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className={`${sizeClasses} flex items-center justify-center shrink-0`}
        style={{ backgroundColor: `${color}20` }}
      >
        <Globe className={iconSizeClasses} style={{ color }} />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClasses} flex items-center justify-center shrink-0 overflow-hidden`}
      style={{ backgroundColor: `${color}15` }}
    >
      <img src={src} alt="" className={`${iconSizeClasses} object-contain`} onError={() => setFailed(true)} />
    </div>
  );
}

// ============================================================================
// Create / Edit Modal
// ============================================================================

interface AppFormData {
  name: string;
  description: string;
  url: string;
  color: string;
  iconUrl: string;
}

const initialFormData: AppFormData = {
  name: "",
  description: "",
  url: "",
  color: "#06b6d4",
  iconUrl: "",
};

function AppFormModal({
  isOpen,
  onClose,
  onSave,
  editingApp,
}: Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AppFormData) => Promise<void>;
  editingApp: App | null;
}>) {
  const [formData, setFormData] = useState<AppFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingApp) {
        setFormData({
          name: editingApp.name,
          description: editingApp.description || "",
          url: editingApp.url,
          color: editingApp.color || "#06b6d4",
          iconUrl: editingApp.iconUrl || "",
        });
      } else {
        setFormData(initialFormData);
      }
    }
  }, [isOpen, editingApp]);

  if (!isOpen) return null;

  const isEdit = !!editingApp;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">{isEdit ? "Edit App" : "Add External App"}</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="app-form-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              App Name <span className="text-red-400">*</span>
            </label>
            <input
              id="app-form-name"
              type="text"
              placeholder="e.g., Jira, Figma, Notion"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>

          <div>
            <label htmlFor="app-form-url" className="block text-sm font-medium text-content-secondary mb-1.5">
              URL <span className="text-red-400">*</span>
            </label>
            <input
              id="app-form-url"
              type="url"
              placeholder="https://example.com"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
            {/* Show auto-detected hint */}
            {formData.url && !formData.iconUrl && detectIconUrlFromAppUrl(formData.url) && (
              <p className="text-xs text-emerald-400 mt-1">App logo will be fetched automatically from the domain</p>
            )}
          </div>

          <div>
            <label htmlFor="app-form-description" className="block text-sm font-medium text-content-secondary mb-1.5">
              Description (optional)
            </label>
            <textarea
              id="app-form-description"
              rows={3}
              placeholder="What does this app do?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
            />
          </div>

          <div>
            <label htmlFor="app-form-icon-url" className="block text-sm font-medium text-content-secondary mb-1.5">
              Custom Icon URL (optional)
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Image className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
                <input
                  id="app-form-icon-url"
                  type="url"
                  placeholder="https://example.com/logo.png"
                  value={formData.iconUrl}
                  onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                  className="w-full pl-10 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              {formData.iconUrl && (
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, iconUrl: "" })}
                  className="p-2 text-content-tertiary hover:text-content-primary transition-colors"
                  title="Clear custom icon"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-content-quaternary mt-1">
              Override the auto-detected icon with a custom image URL.
            </p>
          </div>

          <div>
            <span className="block text-sm font-medium text-content-secondary mb-1.5">Color</span>
            <div className="flex flex-wrap gap-2">
              {APP_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setFormData({ ...formData, color })}
                  className={`h-8 w-8 rounded-lg transition-all ${
                    formData.color === color
                      ? "ring-2 ring-offset-2 ring-offset-surface-primary ring-brand-primary scale-110"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <span className="block text-sm font-medium text-content-secondary mb-1.5">Preview</span>
            <div className="bg-surface-secondary border border-border-secondary rounded-lg p-3 flex items-center gap-3">
              {(() => {
                const previewIconUrl = formData.iconUrl || detectIconUrlFromAppUrl(formData.url);
                if (previewIconUrl) {
                  return (
                    <AppIconImage
                      src={previewIconUrl}
                      color={formData.color}
                      sizeClasses="h-10 w-10 rounded-lg"
                      iconSizeClasses="h-5 w-5"
                    />
                  );
                }
                return (
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${formData.color}20` }}
                  >
                    <Globe className="h-5 w-5" style={{ color: formData.color }} />
                  </div>
                );
              })()}
              <div className="min-w-0">
                <p className="font-medium text-content-primary truncate">{formData.name || "App Name"}</p>
                <p className="text-xs text-content-tertiary truncate">{formData.url || "https://..."}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name.trim() || !formData.url.trim()}
            className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(() => {
              if (saving) {
                return (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    {isEdit ? "Saving..." : "Adding..."}
                  </>
                );
              }
              if (isEdit) return "Save Changes";
              return "Add App";
            })()}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Apps Page
// ============================================================================

export function Apps() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [customApps, setCustomApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);

  const isPlatformAdmin = user?.isPlatformAdmin ?? false;

  const fetchApps = useCallback(async () => {
    try {
      const response = await appService.list();
      setCustomApps(response.data);
    } catch (err) {
      console.error("Failed to fetch apps:", err);
      showToast("Failed to load apps", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  // Merge built-in and custom apps into a unified list
  const allApps: AppEntry[] = useMemo(
    () => [
      ...builtInApps,
      ...(featureFlags.microApps ? [MICRO_APPS_ENTRY] : []),
      ...customApps.map((app): AppEntry => {
        const isExternal = app.url.startsWith("http://") || app.url.startsWith("https://");
        return {
          id: `custom-${app.uuid}`,
          uuid: app.uuid,
          name: app.name,
          description: app.description || "",
          iconColor: app.color || "#06b6d4",
          iconUrl: app.iconUrl || undefined,
          category: isExternal ? "External Tools" : "Document Generation",
          externalUrl: isExternal ? app.url : undefined,
          route: isExternal ? undefined : app.url,
          isBuiltIn: false,
        };
      }),
    ],
    [customApps],
  );

  const filteredApps = useMemo(
    () =>
      allApps.filter((app) => {
        const matchesSearch =
          app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === "All" || app.category === selectedCategory;
        return matchesSearch && matchesCategory;
      }),
    [allApps, searchQuery, selectedCategory],
  );

  const featuredApps = filteredApps.filter((app) => app.featured);
  const otherApps = filteredApps.filter((app) => !app.featured);

  const handleLaunch = (app: AppEntry) => {
    if (app.comingSoon) return;
    if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    } else if (app.route) {
      navigate(app.route);
    }
  };

  const handleSave = async (data: AppFormData) => {
    try {
      if (editingApp) {
        await appService.update(editingApp.uuid, {
          name: data.name.trim(),
          description: data.description.trim() || undefined,
          url: data.url.trim(),
          color: data.color,
          iconUrl: data.iconUrl.trim() || undefined,
        });
        showToast("App updated successfully", "success");
      } else {
        await appService.create({
          name: data.name.trim(),
          description: data.description.trim() || undefined,
          url: data.url.trim(),
          color: data.color,
          icon: "globe",
          iconUrl: data.iconUrl.trim() || undefined,
        });
        showToast("App added successfully", "success");
      }
      setShowFormModal(false);
      setEditingApp(null);
      await fetchApps();
    } catch (err) {
      console.error("Failed to save app:", err);
      showToast(editingApp ? "Failed to update app" : "Failed to add app", "error");
      throw err; // re-throw so modal keeps saving state
    }
  };

  const handleEdit = (app: AppEntry) => {
    const customApp = customApps.find((a) => a.uuid === app.uuid);
    if (!customApp) return;
    setEditingApp(customApp);
    setShowFormModal(true);
  };

  const handleDelete = async (app: AppEntry) => {
    if (!app.uuid) return;

    const confirmed = await confirm({
      title: "Delete App",
      message: `Are you sure you want to delete "${app.name}"? This will remove it from all teams as well.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await appService.delete(app.uuid);
      showToast("App deleted successfully", "success");
      await fetchApps();
    } catch (err) {
      console.error("Failed to delete app:", err);
      showToast("Failed to delete app", "error");
    }
  };

  // Render an app icon — built-in apps have Lucide icons, custom apps use iconUrl or colored Globe
  const renderAppIcon = (app: AppEntry, size: "sm" | "lg") => {
    const sizeClasses = size === "lg" ? "h-14 w-14 rounded-xl" : "h-12 w-12 rounded-xl";
    const iconSizeClasses = size === "lg" ? "h-7 w-7" : "h-6 w-6";

    if (app.icon) {
      const Icon = app.icon;
      return (
        <div
          className={`${sizeClasses} bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0`}
        >
          <Icon className={`${iconSizeClasses} text-brand-primary`} />
        </div>
      );
    }

    // Custom app: use iconUrl image if available, otherwise colored Globe
    const color = app.iconColor || "#06b6d4";
    if (app.iconUrl) {
      return (
        <AppIconImage src={app.iconUrl} color={color} sizeClasses={sizeClasses} iconSizeClasses={iconSizeClasses} />
      );
    }

    return (
      <div
        className={`${sizeClasses} flex items-center justify-center shrink-0`}
        style={{ backgroundColor: `${color}20` }}
      >
        <Globe className={iconSizeClasses} style={{ color }} />
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={Grid3x3}
        title="Custom Apps"
        subtitle="Purpose-built AI applications for your team"
        actions={
          isPlatformAdmin ? (
            <button
              onClick={() => {
                setEditingApp(null);
                setShowFormModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Plus className="h-4 w-4" />
              Add App
            </button>
          ) : undefined
        }
      >
        {/* Search and Filter */}
        <div className="mt-4 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
            <input
              type="text"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? "bg-brand-primary text-white"
                    : "bg-surface-secondary text-content-secondary hover:bg-interactive-active"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin mb-4" />
                <p className="text-content-tertiary">Loading apps...</p>
              </div>
            );
          }
          if (filteredApps.length > 0) {
            return (
              <div className="space-y-8">
                {/* Featured Apps */}
                {featuredApps.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="h-4 w-4 text-yellow-400" />
                      <h2 className="text-lg font-medium text-content-primary">Featured</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {featuredApps.map((app) => (
                        <div
                          key={app.id}
                          role="button" // NOSONAR — contains nested interactive elements
                          tabIndex={0}
                          onClick={() => handleLaunch(app)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLaunch(app);
                            }
                          }}
                          className="bg-linear-to-br from-surface-primary to-surface-secondary border border-border-secondary rounded-xl p-6 hover:border-brand-primary/50 transition-all cursor-pointer group"
                        >
                          <div className="flex items-start gap-4">
                            {renderAppIcon(app, "lg")}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-content-primary">{app.name}</h3>
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                  Featured
                                </span>
                              </div>
                              <p className="text-content-tertiary mt-1">{app.description}</p>
                              <div className="mt-4">
                                <span className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium group-hover:opacity-90 transition-opacity">
                                  Launch App
                                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Apps */}
                {otherApps.length > 0 && (
                  <div>
                    <h2 className="text-lg font-medium text-content-primary mb-4">
                      {featuredApps.length > 0 ? "More Apps" : "Apps"}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {otherApps.map((app) => (
                        <div
                          key={app.id}
                          role="button" // NOSONAR — contains nested interactive elements
                          tabIndex={0}
                          onClick={() => handleLaunch(app)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLaunch(app);
                            }
                          }}
                          className={`bg-surface-primary border border-border-primary rounded-xl p-5 transition-colors group relative ${
                            app.comingSoon
                              ? "opacity-60 cursor-default"
                              : "hover:border-border-secondary cursor-pointer"
                          }`}
                        >
                          {/* Admin actions for custom apps */}
                          {isPlatformAdmin && !app.isBuiltIn && (
                            <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(app);
                                }}
                                className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
                                title="Edit app"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(app);
                                }}
                                className="p-1.5 text-content-tertiary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Delete app"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}

                          <div className="flex items-start gap-4">
                            {renderAppIcon(app, "sm")}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-content-primary truncate">{app.name}</h3>
                                {app.comingSoon && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-surface-tertiary/20 text-content-tertiary">
                                    Coming Soon
                                  </span>
                                )}
                                {app.externalUrl && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-secondary/20 text-brand-secondary flex items-center gap-1">
                                    <ExternalLink className="h-3 w-3" />
                                    External
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-content-tertiary mt-1 line-clamp-2">{app.description}</p>
                            </div>
                          </div>
                          {!app.comingSoon && (
                            <div className="mt-4">
                              <button className="w-full px-4 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors inline-flex items-center justify-center gap-2">
                                {app.externalUrl ? (
                                  <>
                                    Open
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </>
                                ) : (
                                  "Launch"
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
                <LayoutGrid className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-content-primary">No apps found</h3>
              <p className="text-content-tertiary mt-1 text-center max-w-sm">
                {searchQuery || selectedCategory !== "All"
                  ? "Try adjusting your search or filter"
                  : "Apps will appear here when they are available."}
              </p>
              {isPlatformAdmin && !searchQuery && selectedCategory === "All" && (
                <button
                  onClick={() => {
                    setEditingApp(null);
                    setShowFormModal(true);
                  }}
                  className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <Plus className="h-4 w-4" />
                  Add App
                </button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Create/Edit Modal */}
      <AppFormModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditingApp(null);
        }}
        onSave={handleSave}
        editingApp={editingApp}
      />
    </div>
  );
}
